import React, { useState, useEffect, useCallback } from 'react';

const BASE_URL = 'https://cinealert-utah-production.up.railway.app';
const STORAGE_KEY = 'cinealert_admin_secret';
const TMDB_IMG = 'https://image.tmdb.org/t/p/w92';

async function adminFetch(path, secret, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-admin-secret': secret,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const thStyle = {
  padding: '8px 14px',
  textAlign: 'left',
  color: 'var(--text-2)',
  fontWeight: 600,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};
const tdStyle = {
  padding: '10px 14px',
  verticalAlign: 'middle',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
};
const inputStyle = {
  padding: '7px 12px',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--text)',
  fontSize: 13,
  outline: 'none',
  width: '100%',
};
const btnPrimary = {
  padding: '7px 16px',
  background: 'var(--purple)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const btnGhost = {
  padding: '6px 14px',
  background: 'none',
  color: 'var(--text-2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// ─── AdminLogin ───────────────────────────────────────────────────────────────

function AdminLogin({ onLogin }) {
  const [secret, setSecret] = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminFetch('/api/admin/stats', secret.trim());
      localStorage.setItem(STORAGE_KEY, secret.trim());
      onLogin(secret.trim());
    } catch {
      setError('Incorrect password.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)', padding: 16 }}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '32px 28px', width: '100%', maxWidth: 380 }}>
        <h2 style={{ fontSize: 22, fontFamily: 'Outfit, sans-serif', marginBottom: 6 }}>
          Cinema<span style={{ color: 'var(--purple)' }}>Alert</span>
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 24 }}>Admin Dashboard — enter your password</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={secret}
            onChange={e => setSecret(e.target.value)}
            placeholder="Admin password"
            autoFocus
            style={{ ...inputStyle, marginBottom: 12, display: 'block' }}
          />
          {error && <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 10 }}>{error}</p>}
          <button type="submit" disabled={loading || !secret.trim()} style={{ ...btnPrimary, width: '100%', padding: '10px', fontSize: 15, opacity: (!secret.trim() || loading) ? 0.6 : 1 }}>
            {loading ? 'Checking…' : 'Log In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 15, fontFamily: 'Outfit, sans-serif', fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>
        {title}
      </h2>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '16px 20px', flex: '1 1 130px' }}>
      <div style={{ fontSize: 30, fontWeight: 800, fontFamily: 'Outfit, sans-serif', color: accent || 'var(--text)', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ─── MissingUrlRow ────────────────────────────────────────────────────────────

function MissingUrlRow({ movie, secret, onSaved }) {
  const [url, setUrl]     = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState('');
  const [isError, setIsError] = useState(false);

  async function handleSave() {
    if (!url.trim()) return;
    setSaving(true);
    setMsg('');
    setIsError(false);
    try {
      await adminFetch('/api/admin/megaplex-override', secret, {
        method: 'POST',
        body: JSON.stringify({ title: movie.title, megaplex_url: url.trim() }),
      });
      setMsg('Saved!');
      onSaved();
    } catch (err) {
      setIsError(true);
      setMsg(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' }}>
      {/* Poster */}
      {movie.poster_path
        ? <img src={`${TMDB_IMG}${movie.poster_path}`} alt={movie.title} style={{ width: 36, height: 54, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
        : <div style={{ width: 36, height: 54, background: 'var(--bg)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🎬</div>
      }

      {/* Title + date */}
      <div style={{ minWidth: 120, flex: '1 1 120px' }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{movie.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{movie.release_date || 'TBA'}</div>
      </div>

      {/* URL input + save */}
      <div style={{ display: 'flex', gap: 8, flex: '3 1 280px', alignItems: 'center' }}>
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          placeholder="https://megaplex.com/film/HO00004XXX/movie-title"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button onClick={handleSave} disabled={!url.trim() || saving} style={{ ...btnPrimary, opacity: !url.trim() || saving ? 0.5 : 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {msg && (
        <span style={{ fontSize: 12, color: isError ? 'var(--error)' : 'var(--success)', width: '100%', paddingLeft: 48 }}>
          {msg}
        </span>
      )}
    </div>
  );
}

// ─── AllMoviesTable ───────────────────────────────────────────────────────────

function AllMoviesTable({ movies, secret, onUpdated }) {
  const [editingId, setEditingId] = useState(null);
  const [editUrl, setEditUrl]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');
  const [isError, setIsError]     = useState(false);

  const today = new Date().toISOString().split('T')[0];

  function badge(m) {
    if (m.tickets_on_sale)                           return { label: 'On Sale',     color: 'var(--success)' };
    if (m.release_date && m.release_date <= today)   return { label: 'Now Showing', color: 'var(--blue-light)' };
    return                                                  { label: 'Upcoming',    color: 'var(--text-3)' };
  }

  function startEdit(m) {
    setEditingId(m.id);
    setEditUrl(m.megaplex_url || '');
    setSaveMsg('');
    setIsError(false);
  }

  async function saveEdit(movie) {
    setSaving(true);
    setSaveMsg('');
    setIsError(false);
    try {
      await adminFetch('/api/admin/megaplex-override', secret, {
        method: 'POST',
        body: JSON.stringify({ title: movie.title, megaplex_url: editUrl.trim() }),
      });
      setSaveMsg('Saved!');
      setEditingId(null);
      onUpdated();
    } catch (err) {
      setIsError(true);
      setSaveMsg(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={thStyle}>Title</th>
            <th style={thStyle}>Release</th>
            <th style={thStyle}>Status</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Megaplex</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Cinemark</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>On Sale</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {movies.map(m => {
            const b = badge(m);
            const isEditing = editingId === m.id;
            return (
              <React.Fragment key={m.id}>
                <tr style={{ background: isEditing ? 'rgba(124,58,237,0.06)' : 'transparent' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500 }}>{m.title}</span>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                    {m.release_date || '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: b.color, fontWeight: 600, fontSize: 12 }}>{b.label}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span title={m.megaplex_url || 'Missing'}>{m.megaplex_url ? '✅' : '❌'}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span title={m.cinemark_url || 'Missing'}>{m.cinemark_url ? '✅' : '❌'}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {m.tickets_on_sale ? '✅' : '❌'}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => isEditing ? setEditingId(null) : startEdit(m)}
                      style={isEditing ? btnGhost : { ...btnPrimary, padding: '4px 12px', fontSize: 12 }}
                    >
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </td>
                </tr>

                {isEditing && (
                  <tr style={{ background: 'rgba(124,58,237,0.04)' }}>
                    <td colSpan={7} style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>Megaplex URL:</span>
                        <input
                          value={editUrl}
                          onChange={e => setEditUrl(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEdit(m)}
                          placeholder="https://megaplex.com/film/HO00004XXX/movie-title"
                          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                        />
                        <button
                          onClick={() => saveEdit(m)}
                          disabled={saving || !editUrl.trim()}
                          style={{ ...btnPrimary, opacity: saving || !editUrl.trim() ? 0.5 : 1 }}
                        >
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        {saveMsg && (
                          <span style={{ fontSize: 12, color: isError ? 'var(--error)' : 'var(--success)' }}>
                            {saveMsg}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main AdminDashboard ──────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [secret, setSecret]       = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed]       = useState(false);
  const [checking, setChecking]   = useState(true);
  const [stats, setStats]         = useState(null);
  const [movies, setMovies]       = useState([]);
  const [scraperMsg, setScraperMsg]     = useState('');
  const [scraperLoading, setScraperLoading] = useState(false);

  // Add noindex meta so crawlers never index this page
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  // Auto-verify stored secret on mount
  useEffect(() => {
    if (!secret) { setChecking(false); return; }
    adminFetch('/api/admin/stats', secret)
      .then(data  => { setStats(data); setAuthed(true); setChecking(false); })
      .catch(()   => { localStorage.removeItem(STORAGE_KEY); setChecking(false); });
  }, []); // eslint-disable-line

  const loadMovies = useCallback(async () => {
    try {
      const data = await adminFetch('/api/admin/movies', secret);
      setMovies(data.movies);
    } catch {}
  }, [secret]);

  const refreshStats = useCallback(async () => {
    try {
      const data = await adminFetch('/api/admin/stats', secret);
      setStats(data);
    } catch {}
  }, [secret]);

  // Load movies once authenticated
  useEffect(() => {
    if (!authed) return;
    loadMovies();
    refreshStats();
  }, [authed, loadMovies, refreshStats]);

  function handleLogin(s) {
    setSecret(s);
    setAuthed(true);
  }

  function handleLogout() {
    localStorage.removeItem(STORAGE_KEY);
    setSecret('');
    setAuthed(false);
    setMovies([]);
    setStats(null);
  }

  async function runScraper() {
    setScraperMsg('');
    setScraperLoading(true);
    try {
      await adminFetch('/api/admin/scrape', secret, { method: 'POST' });
      setScraperMsg('Scraper triggered successfully.');
    } catch (err) {
      setScraperMsg(`Error: ${err.message}`);
    } finally {
      setScraperLoading(false);
    }
  }

  async function handleMovieUpdated() {
    await Promise.all([loadMovies(), refreshStats()]);
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (checking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)', color: 'var(--text-2)', fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  if (!authed) return <AdminLogin onLogin={handleLogin} />;

  const missingMegaplex = movies.filter(m => !m.megaplex_url);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--text)' }}>

      {/* Header bar */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 17 }}>
          Cinema<span style={{ color: 'var(--purple)' }}>Alert</span>
          <span style={{ color: 'var(--text-3)', fontWeight: 400, fontSize: 13, marginLeft: 10 }}>Admin</span>
        </span>
        <button onClick={handleLogout} style={btnGhost}>Log Out</button>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>

        {/* ── Stats ── */}
        {stats && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
            <StatCard label="Total Movies"          value={stats.total} />
            <StatCard label="Tickets On Sale"       value={stats.on_sale}          accent="var(--success)" />
            <StatCard label="Missing Megaplex URL"  value={stats.missing_megaplex} accent={stats.missing_megaplex > 0 ? 'var(--gold)' : 'var(--success)'} />
            <StatCard label="Missing Cinemark URL"  value={stats.missing_cinemark} accent={stats.missing_cinemark > 0 ? 'var(--gold)' : 'var(--success)'} />
          </div>
        )}

        {/* ── Trigger Scraper ── */}
        <Section title="Trigger Scraper">
          <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button
              onClick={runScraper}
              disabled={scraperLoading}
              style={{ ...btnPrimary, padding: '10px 22px', fontSize: 14, opacity: scraperLoading ? 0.6 : 1 }}
            >
              {scraperLoading ? 'Triggering…' : '▶ Run Scraper Now'}
            </button>
            {scraperMsg && (
              <span style={{ fontSize: 13, color: scraperMsg.startsWith('Error') ? 'var(--error)' : 'var(--success)' }}>
                {scraperMsg}
              </span>
            )}
          </div>
        </Section>

        {/* ── Movies Missing Megaplex URL ── */}
        {missingMegaplex.length > 0 && (
          <Section title={`Movies Missing Megaplex URL (${missingMegaplex.length})`}>
            {missingMegaplex.map(m => (
              <MissingUrlRow
                key={m.id}
                movie={m}
                secret={secret}
                onSaved={handleMovieUpdated}
              />
            ))}
          </Section>
        )}

        {missingMegaplex.length === 0 && movies.length > 0 && (
          <Section title="Movies Missing Megaplex URL">
            <p style={{ padding: '20px 16px', color: 'var(--success)', fontSize: 14 }}>
              ✅ All movies have a Megaplex URL.
            </p>
          </Section>
        )}

        {/* ── All Movies ── */}
        <Section title={`All Movies (${movies.length})`}>
          {movies.length === 0
            ? <p style={{ padding: '20px 16px', color: 'var(--text-2)', fontSize: 13 }}>Loading…</p>
            : <AllMoviesTable movies={movies} secret={secret} onUpdated={handleMovieUpdated} />
          }
        </Section>

      </div>
    </div>
  );
}
